import React, { useEffect, useState } from 'react'
import { Receipt, Printer, XCircle, RefreshCw, CheckCircle2, Clock, BadgeCheck, MessageCircle, FileCheck, Pencil, ShieldAlert, Send, WifiOff, Smartphone } from 'lucide-react'
import api from '../api'
import { isAdmin } from '../auth'
import { PageHeader, Badge, EmptyState, Table, td, Field, Button, Alert, Modal, ModalHeader, Input, Tabs } from '../components/ui'
import InvoiceDoc from '../components/InvoiceDoc'

function rp(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID') }
function bulanIni() { return new Date().toISOString().slice(0, 7) }
function hariIni() { return new Date().toISOString().slice(0, 10) }
// Tombol WA manual (2026-08-11) -- BEDA dari "Kirim WA"/"Kirim Nota" (kirim otomatis lewat bot WA
// tanpa buka HP admin sama sekali). Ini sebaliknya: cuma buka aplikasi WhatsApp di HP admin dgn chat
// pelanggan itu sudah terpilih, isi pesan diketik manual oleh admin sendiri -- supaya admin tidak
// perlu cari-cari kontak pelanggan di HP dulu. Normalisasi nomor SAMA PERSIS dgn normalizePhone() di
// server/pelangganKontakShared.js (duplikat sengaja, cuma dipakai bikin link, bukan jalur keamanan).
function waLink(noHp) {
  let digits = String(noHp || '').replace(/\D/g, '')
  if (!digits) return null
  if (digits.startsWith('0')) digits = '62' + digits.slice(1)
  else if (digits.startsWith('8')) digits = '62' + digits
  if (!digits.startsWith('62') || digits.length < 10 || digits.length > 15) return null
  return `https://wa.me/${digits}`
}
// jatuh_tempo = tanggal isolir (2026-07-29, Isolir Otomatis) -- SATU field yg sama, TIDAK ada
// kolom terpisah, lihat catatan arsitektur di redamanScan.js. Ditampilkan dd/mm/yyyy biar admin
// gampang baca, sumber tetap string YYYY-MM-DD (dipakai apa adanya ke <input type="date"> Edit).
function fmtTgl(iso) {
  if (!iso) return '-'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

const STATUS_TONE = { LUNAS: 'success', SEBAGIAN: 'warning', BELUM_LUNAS: 'danger' }
const STATUS_LABEL = { LUNAS: 'Lunas', SEBAGIAN: 'Sebagian', BELUM_LUNAS: 'Belum Lunas' }

// Sticky Pelanggan+Total Tagihan (2026-07-27, sama alasan spt PelangganKontak.jsx) -- tetap
// menempel di kiri saat tabel digeser ke kanan di HP, supaya identitas pelanggan & jumlah tagihan
// masih kelihatan waktu mau klik tombol Bayar yg letaknya di kolom paling kanan.
const STICKY_COL1_W = 170
const STICKY_COL2_W = 130
// Kolom checkbox seleksi massal (2026-08-10) -- sticky paling kiri, geser offset Pelanggan/Total
// Tagihan yg sebelumnya nempel di left:0/STICKY_COL1_W jadi +CHECKBOX_COL_W.
const CHECKBOX_COL_W = 36
const thStickyStyle = {
  padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11.5, color: 'var(--text-muted)',
  whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)',
  position: 'sticky', zIndex: 2, background: '#fafafc'
}
const tdStickyStyle = { ...td, position: 'sticky', zIndex: 1, background: 'var(--surface)' }

export default function Tagihan() {
  // Generate/Batalkan admin-only, Catat Pembayaran boleh admin+teknisi (teknisi di lapangan sering
  // terima titipan bayar & perlu langsung konfirmasi) -- lihat komentar di server/routes/tagihan.js.
  const admin = isAdmin()
  const [periode, setPeriode] = useState(bulanIni())
  const [tab, setTab] = useState('BELUM_LUNAS')
  const [search, setSearch] = useState('')
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  // Daftar Isolir (2026-08-03) -- LINTAS PERIODE, TIDAK ikut dropdown Periode (lihat komentar
  // GET /tagihan/isolir di server) -- makanya state & load-nya terpisah dari `data`/`load()` yg
  // periode-scoped. Dimuat sekali saat halaman dibuka + dimuat ulang tiap ada aksi yg bisa mengubah
  // status isolir pelanggan (Bayar/Renew/Konfirmasi Keterlambatan).
  const [dataIsolir, setDataIsolir] = useState([])
  const [loadingIsolir, setLoadingIsolir] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [generateInfo, setGenerateInfo] = useState('')
  const [bayarInfo, setBayarInfo] = useState('')

  const [bayarModal, setBayarModal] = useState(null) // item tagihan yg lagi dicatat pembayarannya
  const [jumlahBayar, setJumlahBayar] = useState('')
  const [metodeBayar, setMetodeBayar] = useState('')
  const [keteranganBayar, setKeteranganBayar] = useState('')
  const [bayarError, setBayarError] = useState('')
  const [saving, setSaving] = useState(false)

  const [lihatTagihan, setLihatTagihan] = useState(null)
  const [renewingId, setRenewingId] = useState(null) // id tagihan yg lagi diproses tombol Renew
  const [kirimWaId, setKirimWaId] = useState(null) // id tagihan yg lagi diproses tombol Kirim WA
  const [kirimNotaId, setKirimNotaId] = useState(null) // id tagihan yg lagi diproses tombol Kirim Nota
  const [konfirmasiId, setKonfirmasiId] = useState(null) // id tagihan yg lagi diproses tombol Konfirmasi Keterlambatan

  // Modal Janji Bayar (2026-08-04) -- mengaktifkan Konfirmasi Keterlambatan SEKARANG wajib isi
  // tanggal (lihat komentar toggleKonfirmasi/server). Membatalkan (item sudah dikonfirmasi) TIDAK
  // butuh tanggal, tetap langsung panggil API spt sebelumnya.
  const [konfirmasiModal, setKonfirmasiModal] = useState(null) // item tagihan yg lagi diisi tanggal janji bayarnya
  const [konfirmasiTanggal, setKonfirmasiTanggal] = useState('')
  const [konfirmasiError, setKonfirmasiError] = useState('')

  // Kirim Semua Belum Bayar (2026-07-31) -- admin review dulu daftarnya di modal (data yg SAMA
  // persis dgn yg sedang tampil di tabel/kartu, bukan query baru) sebelum benar2 klik kirim, lihat
  // komentar POST /kirim-massal di server/routes/tagihanWa.js.
  const [massalModal, setMassalModal] = useState(false)
  const [massalSending, setMassalSending] = useState(false)
  const [massalInfo, setMassalInfo] = useState('')

  // Kirim Broadcast Isolir (2026-08-03) -- sama pola dgn "Kirim Semua Belum Bayar" di atas, tapi
  // sumber datanya dataIsolir (lintas periode) & templatenya isolir_notice (POST /kirim-massal-isolir).
  const [massalIsolirModal, setMassalIsolirModal] = useState(false)
  const [massalIsolirSending, setMassalIsolirSending] = useState(false)
  const [massalIsolirInfo, setMassalIsolirInfo] = useState('')

  // Edit Tagihan (2026-07-29) -- admin+teknisi, koreksi biaya_lain/catatan/jatuh_tempo tanpa perlu
  // Batalkan+buat ulang. total_tagihan dihitung ulang di server (harga_bulanan - diskon + biaya_lain baru).
  const [editModal, setEditModal] = useState(null)
  const [editBiayaLain, setEditBiayaLain] = useState('')
  const [editCatatan, setEditCatatan] = useState('')
  const [editJatuhTempo, setEditJatuhTempo] = useState('')
  const [editError, setEditError] = useState('')
  const [editSaving, setEditSaving] = useState(false)

  // Ubah Tanggal Isolir Massal (2026-08-10) -- checkbox per baris + "pilih semua" (tabel & kartu HP
  // sama-sama pakai toggleSelect/toggleSelectAll di bawah) supaya admin bisa geser jatuh_tempo BANYAK
  // tagihan sekaligus (mis. tunda isolir sebulan utk semua yg overdue) tanpa buka Edit satu-satu.
  // BEDA dari modal Edit per-baris: bulk ini HANYA ubah jatuh_tempo, tidak sentuh biaya_lain/catatan/
  // total_tagihan sama sekali -- lihat komentar POST /bulk-jatuh-tempo di server. Seleksi di-reset
  // tiap ganti tab/periode (useEffect di bawah) supaya tidak nyangkut ke baris yg sudah tidak
  // kelihatan lagi di `tampil`.
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [bulkModal, setBulkModal] = useState(false)
  const [bulkTanggal, setBulkTanggal] = useState('')
  const [bulkError, setBulkError] = useState('')
  const [bulkSaving, setBulkSaving] = useState(false)

  useEffect(() => { load() }, [periode])
  useEffect(() => { loadIsolir() }, [])
  useEffect(() => { setSelectedIds(new Set()) }, [tab, periode])

  async function load() {
    setLoading(true)
    try {
      const res = await api.get('/tagihan', { params: { periode } })
      setData(res.data)
    } finally { setLoading(false) }
  }

  async function loadIsolir() {
    setLoadingIsolir(true)
    try {
      const res = await api.get('/tagihan/isolir')
      setDataIsolir(res.data)
    } finally { setLoadingIsolir(false) }
  }

  async function generate() {
    setGenerating(true)
    setGenerateInfo('')
    try {
      const res = await api.post('/tagihan/generate', { periode })
      setGenerateInfo(`${res.data.dibuat} tagihan baru dibuat, ${res.data.dilewati} dilewati (sudah ada/paket belum diset).`)
      load()
    } finally { setGenerating(false) }
  }

  function bukaBayar(item) {
    setBayarModal(item)
    setJumlahBayar(''); setMetodeBayar(''); setKeteranganBayar(''); setBayarError('')
    setBayarInfo('')
  }

  async function simpanBayar() {
    setBayarError('')
    const jumlah = Number(jumlahBayar)
    if (!jumlah || jumlah <= 0) return setBayarError('Jumlah pembayaran harus lebih dari 0')
    setSaving(true)
    try {
      const res = await api.post(`/tagihan/${bayarModal.id}/pembayaran`, { jumlah, metode: metodeBayar, keterangan: keteranganBayar })
      setBayarModal(null)
      setBayarInfo(res.data.masuk_deposit > 0
        ? `Pembayaran tercatat. Rp ${Number(res.data.masuk_deposit).toLocaleString('id-ID')} kelebihan otomatis masuk dompet pelanggan (dipakai saat generate tagihan bulan depan).`
        : '')
      load()
      loadIsolir()
      if (lihatTagihan) bukaLihat(lihatTagihan.id)
    } catch (e) {
      setBayarError(e.response?.data?.message || 'Gagal mencatat pembayaran')
    } finally { setSaving(false) }
  }

  // Renew -- catat pembayaran PENUH sesuai sisa tagihan sekali klik, tanpa isi Jumlah manual (beda
  // dari "Bayar" yg tetap lewat modal utk pembayaran sebagian/custom). Pakai endpoint yg SAMA
  // (POST /:id/pembayaran) cuma jumlah-nya dihitung otomatis = sisa tagihan, jadi tidak pernah
  // overpay/kelebihan masuk dompet (lihat terimaPembayaran() di pembayaranShared.js).
  async function renewPenuh(item) {
    const sisa = item.total_tagihan - item.total_dibayar
    if (sisa <= 0) return
    if (!window.confirm(`Renew — catat pembayaran penuh ${rp(sisa)} untuk ${item.nama_pelanggan || item.ppp_username}?`)) return
    setRenewingId(item.id)
    try {
      await api.post(`/tagihan/${item.id}/pembayaran`, { jumlah: sisa, metode: 'Renew', keterangan: 'Renew - pembayaran penuh' })
      setBayarInfo('Pembayaran penuh tercatat via Renew.')
      load()
      loadIsolir()
      if (lihatTagihan && lihatTagihan.id === item.id) bukaLihat(item.id)
    } catch (e) {
      window.alert(e.response?.data?.message || 'Gagal memproses Renew')
    } finally { setRenewingId(null) }
  }

  // Kirim WA -- fase 1 "Template WA Tagihan" (2026-07-26): kirim pesan tagihan (template + link
  // invoice publik) ke nomor WA pelanggan lewat POST /tagihan-wa/:id/kirim (lihat server/routes/tagihanWa.js).
  async function kirimWa(item) {
    setKirimWaId(item.id)
    try {
      await api.post(`/tagihan-wa/${item.id}/kirim`)
      setBayarInfo(`WA tagihan terkirim ke ${item.nama_pelanggan || item.ppp_username}.`)
    } catch (e) {
      window.alert(e.response?.data?.message || 'Gagal mengirim WA tagihan')
    } finally { setKirimWaId(null) }
  }

  // Kirim Nota (2026-07-27) -- beda dari Kirim WA di atas: dikirim SETELAH ada pembayaran tercatat
  // (bukan sebelum), manual only (belum ada toggle auto-kirim spt tagihan_baru), lewat POST
  // /tagihan-wa/:id/kirim-nota.
  async function kirimNota(item) {
    setKirimNotaId(item.id)
    try {
      await api.post(`/tagihan-wa/${item.id}/kirim-nota`)
      setBayarInfo(`Nota pembayaran terkirim ke ${item.nama_pelanggan || item.ppp_username}.`)
    } catch (e) {
      window.alert(e.response?.data?.message || 'Gagal mengirim nota pembayaran')
    } finally { setKirimNotaId(null) }
  }

  // Kirim Semua Belum Bayar -- kirim WA tagihan (template tagihan_baru, sama isinya dgn tombol
  // "Kirim WA" per-baris) ke SEMUA tagihan periode ini yg status-nya BELUM_LUNAS persis (belum
  // dibayar sedikitpun). Yang SEBAGIAN sengaja diabaikan/tidak ikut (permintaan eksplisit). Id yg
  // dikirim ke server = persis daftar yg tampil di modal review, bukan server query ulang.
  const dataBelumBayarSamaSekali = data.filter(item => item.status === 'BELUM_LUNAS')

  async function kirimMassal() {
    setMassalSending(true)
    setMassalInfo('')
    try {
      const res = await api.post('/tagihan-wa/kirim-massal', { tagihanIds: dataBelumBayarSamaSekali.map(item => item.id) })
      setMassalModal(false)
      setBayarInfo(res.data.message)
    } catch (e) {
      setMassalInfo(e.response?.data?.message || 'Gagal memulai pengiriman massal')
    } finally { setMassalSending(false) }
  }

  // Kirim Broadcast Isolir -- kirim WA template isolir_notice ke SEMUA pelanggan yg sedang tampil
  // di tab Isolir (dataIsolirDicari, ikut pencarian yg lagi aktif). Sama pola dgn kirimMassal() di
  // atas: id yg dikirim persis daftar yg direview admin di modal, proses jalan di background.
  async function kirimMassalIsolir() {
    setMassalIsolirSending(true)
    setMassalIsolirInfo('')
    try {
      const res = await api.post('/tagihan-wa/kirim-massal-isolir', { tagihanIds: dataIsolirDicari.map(item => item.id) })
      setMassalIsolirModal(false)
      setBayarInfo(res.data.message)
    } catch (e) {
      setMassalIsolirInfo(e.response?.data?.message || 'Gagal memulai pengiriman broadcast isolir')
    } finally { setMassalIsolirSending(false) }
  }

  // Konfirmasi Keterlambatan (2026-07-29, Isolir Otomatis; jadi "Janji Bayar" 2026-08-04) -- selama
  // aktif, tagihan ini DIKECUALIKAN dari cron syncIsolirStatus walau lewat jatuh_tempo & belum
  // dibayar sama sekali (dipakai saat pelanggan chat minta tunda). Pengecualian ini SEKARANG
  // berbatas tanggal (bukan permanen) -- lihat komentar server routes/tagihan.js & janjiBayarScan.js:
  // tepat di tanggal yg diisi, pelanggan otomatis dikirimi WA pengingat + diisolir kalau masih belum
  // bayar. Mengaktifkan lewat modal (wajib isi tanggal), membatalkan langsung tanpa modal.
  async function toggleKonfirmasi(item, tanggalJanjiBayar) {
    setKonfirmasiId(item.id)
    try {
      const res = await api.post(`/tagihan/${item.id}/konfirmasi-keterlambatan`, tanggalJanjiBayar ? { tanggal_janji_bayar: tanggalJanjiBayar } : undefined)
      // Info sukses (isi tanggal janji bayar / tagihan periode berjalan auto-generate) -- lihat
      // komentar server, munculkan biar admin tahu tanpa perlu tebak2.
      setBayarInfo(res.data.message)
      load()
      loadIsolir()
      if (lihatTagihan && lihatTagihan.id === item.id) bukaLihat(item.id)
      return true
    } catch (e) {
      window.alert(e.response?.data?.message || 'Gagal mengubah konfirmasi keterlambatan')
      return false
    } finally { setKonfirmasiId(null) }
  }

  function bukaKonfirmasiModal(item) {
    setKonfirmasiModal(item)
    setKonfirmasiTanggal('')
    setKonfirmasiError('')
  }

  async function simpanKonfirmasi() {
    setKonfirmasiError('')
    if (!konfirmasiTanggal) return setKonfirmasiError('Tanggal janji bayar wajib diisi')
    const ok = await toggleKonfirmasi(konfirmasiModal, konfirmasiTanggal)
    if (ok) setKonfirmasiModal(null)
  }

  async function bukaLihat(id) {
    const res = await api.get(`/tagihan/${id}`)
    setLihatTagihan(res.data)
  }

  async function batalkanTagihan(id) {
    if (!window.confirm('Batalkan tagihan ini? Semua pembayaran yang tercatat di tagihan ini ikut terhapus.')) return
    await api.delete(`/tagihan/${id}`)
    setLihatTagihan(null)
    load()
  }

  function bukaEditTagihan(item) {
    setEditModal(item)
    setEditBiayaLain(String(item.biaya_lain ?? 0))
    setEditCatatan(item.catatan_biaya_lain || '')
    setEditJatuhTempo(item.jatuh_tempo || '')
    setEditError('')
  }

  async function simpanEditTagihan() {
    setEditError('')
    const biayaLain = Number(editBiayaLain)
    if (isNaN(biayaLain) || biayaLain < 0) return setEditError('Biaya lain harus angka >= 0')
    if (!editJatuhTempo) return setEditError('Jatuh tempo wajib diisi')
    setEditSaving(true)
    try {
      await api.put(`/tagihan/${editModal.id}`, {
        biaya_lain: biayaLain, catatan_biaya_lain: editCatatan, jatuh_tempo: editJatuhTempo
      })
      setEditModal(null)
      load()
      if (lihatTagihan && lihatTagihan.id === editModal.id) bukaLihat(editModal.id)
    } catch (e) {
      setEditError(e.response?.data?.message || 'Gagal menyimpan perubahan')
    } finally { setEditSaving(false) }
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    setSelectedIds(prev => prev.size === tampil.length ? new Set() : new Set(tampil.map(item => item.id)))
  }

  function bukaBulkModal() {
    setBulkTanggal('')
    setBulkError('')
    setBulkModal(true)
  }

  async function simpanBulkTanggal() {
    setBulkError('')
    if (!bulkTanggal) return setBulkError('Tanggal isolir wajib diisi')
    setBulkSaving(true)
    try {
      const res = await api.post('/tagihan/bulk-jatuh-tempo', { ids: [...selectedIds], jatuh_tempo: bulkTanggal })
      setBulkModal(false)
      setSelectedIds(new Set())
      setBayarInfo(res.data.message)
      load()
      loadIsolir()
    } catch (e) {
      setBulkError(e.response?.data?.message || 'Gagal mengubah tanggal isolir')
    } finally { setBulkSaving(false) }
  }

  // Dipakai bareng oleh tabel (desktop) & kartu (HP) -- lihat komentar .list-cards di index.css.
  function aksiTagihan(item) {
    const wa = waLink(item.no_hp)
    return (
      <>
        {wa && (
          <Button size="sm" variant="soft" icon={Smartphone} style={{ color: '#25D366' }}
            onClick={() => window.open(wa, '_blank', 'noopener')}
            title={`Buka WhatsApp -- chat manual ke ${item.nama_pelanggan || item.ppp_username}`}
          />
        )}
        {item.status !== 'LUNAS' && <Button size="sm" onClick={() => bukaBayar(item)}>Bayar</Button>}
        {item.status !== 'LUNAS' && (
          <Button size="sm" variant="success" icon={CheckCircle2} loading={renewingId === item.id} onClick={() => renewPenuh(item)}>Renew</Button>
        )}
        {item.status !== 'LUNAS' && (
          <Button size="sm" variant="soft" icon={MessageCircle} loading={kirimWaId === item.id} onClick={() => kirimWa(item)}>Kirim WA</Button>
        )}
        {item.total_dibayar > 0 && (
          <Button size="sm" variant="soft" icon={FileCheck} loading={kirimNotaId === item.id} onClick={() => kirimNota(item)}>Kirim Nota</Button>
        )}
        {item.status !== 'LUNAS' && (
          <Button
            size="sm"
            variant={item.konfirmasi_keterlambatan ? 'success' : 'soft'}
            icon={ShieldAlert}
            loading={konfirmasiId === item.id}
            onClick={() => item.konfirmasi_keterlambatan ? toggleKonfirmasi(item) : bukaKonfirmasiModal(item)}
            title={item.konfirmasi_keterlambatan
              ? 'Batalkan pengecualian isolir -- tagihan ini akan kembali dianggap bermasalah spt biasa'
              : 'Janji bayar -- kecualikan sementara dari isolir otomatis sampai tanggal yang disepakati'}
          >
            {item.konfirmasi_keterlambatan
              ? (item.konfirmasi_keterlambatan_sampai ? `Janji Bayar s.d. ${fmtTgl(item.konfirmasi_keterlambatan_sampai)}` : 'Dikonfirmasi (tanpa batas)')
              : 'Konfirmasi Keterlambatan'}
          </Button>
        )}
        <Button size="sm" variant="soft" icon={Pencil} onClick={() => bukaEditTagihan(item)}>Edit</Button>
        <Button size="sm" variant="soft" onClick={() => bukaLihat(item.id)}>Detail</Button>
      </>
    )
  }

  function cocokPencarian(item, q) {
    return (item.nama_pelanggan || '').toLowerCase().includes(q) ||
      (item.ppp_username || '').toLowerCase().includes(q) ||
      (item.nama_paket || '').toLowerCase().includes(q)
  }

  const q = search.trim().toLowerCase()
  const dataDicari = !q ? data : data.filter(item => cocokPencarian(item, q))
  const dataIsolirDicari = !q ? dataIsolir : dataIsolir.filter(item => cocokPencarian(item, q))
  const dataBelumLunas = dataDicari.filter(item => item.status !== 'LUNAS')
  const dataLunas = dataDicari.filter(item => item.status === 'LUNAS')
  const tampil = tab === 'LUNAS' ? dataLunas : tab === 'ISOLIR' ? dataIsolirDicari : dataBelumLunas
  const sedangMemuat = tab === 'ISOLIR' ? loadingIsolir : loading
  const pesanKosong = q ? 'Tidak ada hasil untuk pencarian ini'
    : tab === 'LUNAS' ? 'Belum ada yang lunas periode ini'
    : tab === 'ISOLIR' ? 'Tidak ada pelanggan yang sedang isolir'
    : 'Semua pelanggan sudah lunas periode ini'

  return (
    <div>
      <PageHeader title="Tagihan" subtitle="Tagihan bulanan pelanggan & pencatatan pembayaran" />

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
        <Field label="Periode">
          <Input type="month" value={periode} onChange={e => setPeriode(e.target.value)} style={{ width: 180 }} />
        </Field>
        {admin && (
          <Button variant="soft" icon={RefreshCw} loading={generating} onClick={generate}>Generate Tagihan Bulan Ini</Button>
        )}
        {admin && (
          <Button variant="soft" icon={Send} onClick={() => setMassalModal(true)} disabled={dataBelumBayarSamaSekali.length === 0}>
            Kirim Semua Belum Bayar ({dataBelumBayarSamaSekali.length})
          </Button>
        )}
        {admin && (
          <Button variant="soft" icon={WifiOff} onClick={() => setMassalIsolirModal(true)} disabled={dataIsolir.length === 0}>
            Kirim Broadcast Isolir ({dataIsolir.length})
          </Button>
        )}
        <Field label="Cari" style={{ flex: 1, minWidth: 220 }}>
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari username / nama pelanggan / paket..." />
        </Field>
        {/* Duplikat checkbox header tabel (2026-08-10) -- di tampilan kartu HP tidak ada header
            tabel utk toggle-semua, tombol ini yg jadi satu-satunya cara select-all di layar sempit. */}
        {tampil.length > 0 && (
          <Button variant="neutral" onClick={toggleSelectAll}>
            {selectedIds.size === tampil.length ? 'Batal Pilih Semua' : `Pilih Semua (${tampil.length})`}
          </Button>
        )}
      </div>

      {generateInfo && <Alert type="success">{generateInfo}</Alert>}
      {bayarInfo && <Alert type="success">{bayarInfo}</Alert>}

      <Tabs
        tabs={[
          ['BELUM_LUNAS', `Belum Lunas (${dataBelumLunas.length})`, Clock],
          ['LUNAS', `Lunas (${dataLunas.length})`, BadgeCheck],
          ['ISOLIR', `Isolir (${dataIsolirDicari.length})`, WifiOff],
        ]}
        active={tab} onChange={setTab}
      />

      {/* Bar aksi seleksi massal (2026-08-10) -- muncul begitu ada checkbox tercentang, di baris
          manapun (tab BELUM_LUNAS/LUNAS/ISOLIR sama-sama boleh, tidak dibatasi status spt tombol
          Edit per-baris). */}
      {selectedIds.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--brand-soft)', color: 'var(--brand-soft-text)', borderRadius: 10, padding: '10px 16px', marginBottom: 14, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 13 }}>{selectedIds.size} tagihan terpilih</strong>
          <Button size="sm" onClick={bukaBulkModal}>Ubah Tanggal Isolir</Button>
          <Button size="sm" variant="neutral" onClick={() => setSelectedIds(new Set())}>Batal Pilih</Button>
        </div>
      )}

      {/* Desktop: tabel sticky. HP (<=860px): kartu -- lihat komentar .list-cards di index.css
          (sticky 2 kolom di layar sempit makan ~77% lebar, sisa scroll < 1 kolom). Keduanya SELALU
          di-render, CSS yg pilih mana tampil (mirror pola sidebar/drawer), jadi actionAksi() dipakai
          bareng oleh tabel & kartu supaya tombol Bayar/Renew/dst tidak dobel-tulis. */}
      <div className="list-table">
      <Table>
        <thead>
          <tr style={{ background: '#fafafc' }}>
            <th style={{ ...thStickyStyle, left: 0, minWidth: CHECKBOX_COL_W, textAlign: 'center' }}>
              <input type="checkbox" checked={tampil.length > 0 && selectedIds.size === tampil.length} onChange={toggleSelectAll} style={{ width: 15, height: 15, accentColor: 'var(--brand)', cursor: 'pointer' }} />
            </th>
            <th style={{ ...thStickyStyle, left: CHECKBOX_COL_W, minWidth: STICKY_COL1_W }}>Pelanggan</th>
            <th style={{ ...thStickyStyle, left: CHECKBOX_COL_W + STICKY_COL1_W, minWidth: STICKY_COL2_W, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}>Total Tagihan</th>
            {['Paket', 'Total Dibayar', 'Tgl Isolir', 'Status', ''].map(h => (
              <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sedangMemuat ? (
            <tr><td colSpan={8}><EmptyState icon={Receipt} title="Memuat..." /></td></tr>
          ) : tampil.length === 0 ? (
            <tr><td colSpan={8}><EmptyState icon={Receipt} title={pesanKosong} /></td></tr>
          ) : tampil.map(item => (
            <tr key={item.id}>
              <td style={{ ...tdStickyStyle, left: 0, minWidth: CHECKBOX_COL_W, textAlign: 'center' }}>
                <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleSelect(item.id)} style={{ width: 15, height: 15, accentColor: 'var(--brand)', cursor: 'pointer' }} />
              </td>
              <td style={{ ...tdStickyStyle, left: CHECKBOX_COL_W, minWidth: STICKY_COL1_W, fontWeight: 600 }}>{item.nama_pelanggan || item.ppp_username}<div style={{ fontWeight: 400, fontSize: 11.5, color: 'var(--text-muted)' }}>{item.ppp_username}</div></td>
              <td style={{ ...tdStickyStyle, left: CHECKBOX_COL_W + STICKY_COL1_W, minWidth: STICKY_COL2_W, boxShadow: '2px 0 4px rgba(0,0,0,0.06)' }}>{rp(item.total_tagihan)}{item.diskon > 0 && <div title={item.catatan_diskon || 'Diskon'} style={{ fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>🎁 −{rp(item.diskon)}</div>}</td>
              <td style={td}>{item.nama_paket}</td>
              <td style={td}>{rp(item.total_dibayar)}</td>
              <td style={td}>{fmtTgl(item.jatuh_tempo)}</td>
              <td style={td}><Badge tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</Badge></td>
              <td style={td}>
                <div style={{ display: 'flex', gap: 8 }}>{aksiTagihan(item)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      </div>

      <div className="list-cards">
        {sedangMemuat ? (
          <EmptyState icon={Receipt} title="Memuat..." />
        ) : tampil.length === 0 ? (
          <EmptyState icon={Receipt} title={pesanKosong} />
        ) : tampil.map(item => (
          <div className="card-row" key={item.id}>
            <div className="card-row-top">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleSelect(item.id)} style={{ width: 16, height: 16, marginTop: 3, accentColor: 'var(--brand)', cursor: 'pointer', flexShrink: 0 }} />
                <div>
                  <div className="card-row-title">{item.nama_pelanggan || item.ppp_username}</div>
                  <div className="card-row-sub">{item.ppp_username} · {item.nama_paket}</div>
                </div>
              </div>
              <Badge tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</Badge>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Total Tagihan</div>
                <div style={{ fontWeight: 700, fontSize: 17 }}>{rp(item.total_tagihan)}</div>
                {item.diskon > 0 && <div style={{ fontSize: 11.5, color: 'var(--success)', fontWeight: 600 }}>🎁 {item.catatan_diskon || 'Diskon'} −{rp(item.diskon)}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Dibayar</div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-muted)' }}>{rp(item.total_dibayar)}</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              Tgl Isolir: <strong>{fmtTgl(item.jatuh_tempo)}</strong>
              {item.konfirmasi_keterlambatan ? ` · Janji bayar s.d. ${item.konfirmasi_keterlambatan_sampai ? fmtTgl(item.konfirmasi_keterlambatan_sampai) : '(tanpa batas)'}` : ''}
            </div>
            <div className="card-row-actions">{aksiTagihan(item)}</div>
          </div>
        ))}
      </div>

      {/* Modal catat pembayaran -- admin+teknisi */}
      {bayarModal && (
        <Modal onClose={() => setBayarModal(null)} width={400}>
          <ModalHeader title={`Catat Pembayaran — ${bayarModal.nama_pelanggan || bayarModal.ppp_username}`} subtitle={`Sisa tagihan: ${rp(bayarModal.total_tagihan - bayarModal.total_dibayar)}`} onClose={() => setBayarModal(null)} />
          {bayarError && <Alert type="error">{bayarError}</Alert>}
          <Field label="Jumlah (Rp) *" style={{ marginBottom: 14 }}>
            <Input type="number" value={jumlahBayar} onChange={e => setJumlahBayar(e.target.value)} placeholder="0" />
          </Field>
          <Field label="Metode" style={{ marginBottom: 14 }}>
            <Input value={metodeBayar} onChange={e => setMetodeBayar(e.target.value)} placeholder="Tunai / Transfer / dll" />
          </Field>
          <Field label="Keterangan" style={{ marginBottom: 22 }}>
            <Input value={keteranganBayar} onChange={e => setKeteranganBayar(e.target.value)} placeholder="Opsional" />
          </Field>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setBayarModal(null)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={saving} onClick={simpanBayar} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}

      {/* Modal Janji Bayar (2026-08-04) -- wajib isi tanggal saat mengaktifkan Konfirmasi
          Keterlambatan. Tanggal ini jadi batas: cron janjiBayarScan.js kirim WA pengingat ke
          pelanggan + grup Faznet TEPAT di tanggal itu, lalu isolir instan kalau masih belum
          dibayar -- lihat komentar server/janjiBayarScan.js. */}
      {konfirmasiModal && (
        <Modal onClose={() => setKonfirmasiModal(null)} width={400}>
          <ModalHeader title={`Konfirmasi Keterlambatan — ${konfirmasiModal.nama_pelanggan || konfirmasiModal.ppp_username}`} subtitle="Isi tanggal janji bayar yang disepakati" onClose={() => setKonfirmasiModal(null)} />
          {konfirmasiError && <Alert type="error">{konfirmasiError}</Alert>}
          <Field label="Janji Bayar Sampai Tanggal *" style={{ marginBottom: 6 }}>
            <Input type="date" min={hariIni()} value={konfirmasiTanggal} onChange={e => setKonfirmasiTanggal(e.target.value)} />
          </Field>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 22 }}>
            Pelanggan dikecualikan dari isolir otomatis sampai tanggal ini. Tepat di tanggal itu, sistem otomatis kirim WA pengingat "hari terakhir bayar" ke pelanggan & grup Faznet -- kalau masih belum dibayar, pelanggan langsung diisolir hari itu juga.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" disabled={konfirmasiId === konfirmasiModal.id} onClick={() => setKonfirmasiModal(null)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={konfirmasiId === konfirmasiModal.id} onClick={simpanKonfirmasi} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}

      {/* Modal Edit Tagihan (2026-07-29) -- admin+teknisi, koreksi biaya_lain/catatan/jatuh_tempo.
          harga_bulanan (harga paket) sengaja ditampilkan read-only saja -- total_tagihan dihitung
          ulang di server dari harga_bulanan + biaya_lain baru, lihat komentar di routes/tagihan.js. */}
      {editModal && (
        <Modal onClose={() => setEditModal(null)} width={400}>
          <ModalHeader title={`Edit Tagihan — ${editModal.nama_pelanggan || editModal.ppp_username}`} subtitle={`Harga paket: ${rp(editModal.harga_bulanan)}${editModal.diskon > 0 ? ` − diskon ${rp(editModal.diskon)}` : ''}`} onClose={() => setEditModal(null)} />
          {editError && <Alert type="error">{editError}</Alert>}
          <Field label="Biaya Lain (Rp)" style={{ marginBottom: 14 }}>
            <Input type="number" value={editBiayaLain} onChange={e => setEditBiayaLain(e.target.value)} placeholder="0" />
          </Field>
          <Field label="Catatan Biaya Lain" style={{ marginBottom: 14 }}>
            <Input value={editCatatan} onChange={e => setEditCatatan(e.target.value)} placeholder="Opsional, mis. Biaya Instalasi" />
          </Field>
          <Field label="Jatuh Tempo / Tanggal Isolir *" style={{ marginBottom: 6 }}>
            <Input type="date" value={editJatuhTempo} onChange={e => setEditJatuhTempo(e.target.value)} />
          </Field>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 14 }}>
            Kalau lewat tanggal ini & belum ada pembayaran sama sekali (dan belum dikonfirmasi keterlambatan), pelanggan akan diisolir otomatis.
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 22 }}>
            Total tagihan baru: <strong>{rp((Number(editBiayaLain) || 0) + (editModal.harga_bulanan || 0) - (editModal.diskon || 0))}</strong>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setEditModal(null)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={editSaving} onClick={simpanEditTagihan} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}

      {/* Modal Ubah Tanggal Isolir Massal (2026-08-10) -- HANYA ubah jatuh_tempo, beda dari modal
          Edit per-baris yg jg bisa koreksi biaya_lain/catatan. Berlaku ke SEMUA id yg tercentang
          (selectedIds), lintas status/tab yg lagi aktif saat centang dilakukan. */}
      {bulkModal && (
        <Modal onClose={() => !bulkSaving && setBulkModal(false)} width={400}>
          <ModalHeader title="Ubah Tanggal Isolir Massal" subtitle={`${selectedIds.size} tagihan terpilih`} onClose={() => !bulkSaving && setBulkModal(false)} />
          {bulkError && <Alert type="error">{bulkError}</Alert>}
          <Field label="Tanggal Isolir Baru *" style={{ marginBottom: 6 }}>
            <Input type="date" value={bulkTanggal} onChange={e => setBulkTanggal(e.target.value)} />
          </Field>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 22 }}>
            Tanggal ini akan diterapkan ke semua {selectedIds.size} tagihan terpilih, menggantikan tanggal isolir masing-masing sebelumnya. Biaya/catatan tagihan tidak ikut berubah.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" disabled={bulkSaving} onClick={() => setBulkModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={bulkSaving} onClick={simpanBulkTanggal} style={{ flex: 1, justifyContent: 'center' }}>Terapkan ke Semua</Button>
          </div>
        </Modal>
      )}

      {/* Modal review "Kirim Semua Belum Bayar" -- admin cek dulu daftar pelanggan yg akan dikirimi
          WA (persis data yg lagi tampil, SEBAGIAN sengaja tidak diikutkan) sebelum benar2 klik kirim.
          Proses kirim jalan di background di server (delay anti-spam per pesan), respons di sini
          cuma konfirmasi "sudah dimulai" -- hasil akhir (berapa terkirim/gagal) ada di Log Sistem. */}
      {massalModal && (
        <Modal onClose={() => !massalSending && setMassalModal(false)} width={480}>
          <ModalHeader title="Kirim Semua Tagihan Belum Bayar" subtitle={`${dataBelumBayarSamaSekali.length} pelanggan, periode ${periode} -- belum bayar sama sekali`} onClose={() => !massalSending && setMassalModal(false)} />
          {massalInfo && <Alert type="error">{massalInfo}</Alert>}
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 14 }}>
            Tagihan yang sudah dibayar sebagian TIDAK ikut dikirim. Cek daftar di bawah sebelum lanjut:
          </p>
          <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 20 }}>
            {dataBelumBayarSamaSekali.length === 0 ? (
              <div style={{ padding: 16, fontSize: 12.5, color: 'var(--text-muted)' }}>Tidak ada tagihan yang belum dibayar sama sekali di periode ini.</div>
            ) : dataBelumBayarSamaSekali.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
                <span>{item.nama_pelanggan || item.ppp_username}</span>
                <span style={{ color: 'var(--text-muted)' }}>{rp(item.total_tagihan)}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" disabled={massalSending} onClick={() => setMassalModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={massalSending} disabled={dataBelumBayarSamaSekali.length === 0} onClick={kirimMassal} style={{ flex: 1, justifyContent: 'center' }}>Kirim Semua Sekarang</Button>
          </div>
        </Modal>
      )}

      {/* Modal review "Kirim Broadcast Isolir" (2026-08-03) -- sama pola dgn modal Kirim Semua Belum
          Bayar di atas: review dulu daftar pelanggan yg SEDANG isolir sebelum kirim, proses jalan
          background, hasil di Log Sistem. Template WA yg dipakai = isolir_notice. Tombol manual
          per-baris "Kirim Notif Isolir" DIHAPUS 2026-08-04 (permintaan user, dianggap
          redundan/membingungkan di samping tombol ini + auto-trigger isolirSync.js) -- endpoint
          POST /tagihan-wa/:id/kirim-isolir sengaja TETAP ADA di server (dipakai kirim-massal-isolir
          di bawah & auto-trigger), jangan dihapus. */}
      {massalIsolirModal && (
        <Modal onClose={() => !massalIsolirSending && setMassalIsolirModal(false)} width={480}>
          <ModalHeader title="Kirim Broadcast Isolir" subtitle={`${dataIsolirDicari.length} pelanggan sedang isolir`} onClose={() => !massalIsolirSending && setMassalIsolirModal(false)} />
          {massalIsolirInfo && <Alert type="error">{massalIsolirInfo}</Alert>}
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 14 }}>
            WA notif isolir (template <code>isolir_notice</code>) akan dikirim ke semua pelanggan di bawah ini. Cek daftar sebelum lanjut:
          </p>
          <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 20 }}>
            {dataIsolirDicari.length === 0 ? (
              <div style={{ padding: 16, fontSize: 12.5, color: 'var(--text-muted)' }}>Tidak ada pelanggan yang sedang isolir.</div>
            ) : dataIsolirDicari.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
                <span>{item.nama_pelanggan || item.ppp_username}</span>
                <span style={{ color: 'var(--text-muted)' }}>{rp(item.total_tagihan)}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" disabled={massalIsolirSending} onClick={() => setMassalIsolirModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={massalIsolirSending} disabled={dataIsolirDicari.length === 0} onClick={kirimMassalIsolir} style={{ flex: 1, justifyContent: 'center' }}>Kirim Broadcast Sekarang</Button>
          </div>
        </Modal>
      )}

      {/* Modal detail/cetak -- WYSIWYG pakai InvoiceDoc yg sama dgn halaman cetak (pola sama persis
          dgn Payroll.jsx + SlipDoc.jsx). admin+teknisi bisa lihat, cetak, & Edit; Batalkan admin-only. */}
      {lihatTagihan && (
        <Modal onClose={() => setLihatTagihan(null)} width={640}>
          <ModalHeader title="Detail Tagihan" onClose={() => setLihatTagihan(null)} />
          <InvoiceDoc tagihan={lihatTagihan} />

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            {admin && <Button variant="danger-soft" icon={XCircle} onClick={() => batalkanTagihan(lihatTagihan.id)}>Batalkan</Button>}
            <Button variant="soft" icon={Pencil} onClick={() => bukaEditTagihan(lihatTagihan)}>Edit</Button>
            <Button icon={Printer} onClick={() => window.open(`/tagihan/${lihatTagihan.id}/cetak`, '_blank')} style={{ flex: 1, justifyContent: 'center' }}>Cetak</Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
